import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type SummaryType = 'general' | 'focused' | null;

export type SummaryConfigs = {
  EntityId: string;
  DefaultSummaryType: SummaryType;
  Timeframe: string;
  Checklist: string;
};

interface UseConfigsResult {
  configs: SummaryConfigs | null;
  loading: boolean;
  error: Error | null;
  updateConfigs: (newConfigs: SummaryConfigs) => Promise<void>;
  fetchConfigs: () => Promise<void>;
}

/**
 * Custom hook to fetch and update all summary configurations for a builder ID.
 * @param {string} builderId The builder ID.
 * @returns {UseConfigsResult} An object containing the configs, loading state, error state, and an update function.
 */
function useSummaryConfig(builderId: string): UseConfigsResult {
  const [configs, setConfigs] = useState<SummaryConfigs | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const telemetry = useTelemetry();
  const { requestContext, ctwFetch } = useCTW();

  const apiUrl = useMemo(() => {
    if (!builderId) {
      return null;
    }
    return `${getApiServiceBaseUrl({ env: requestContext.env, service: 'api.ai' })}/config/${builderId}`;
  }, [builderId, requestContext.env]);

  const fetchConfigs = useCallback(async () => {
    if (!apiUrl) {
      setConfigs(null);
      setLoading(false);
      setError(new Error('Builder ID is required.'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ctwFetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${requestContext.authToken}`,
          'Zus-Account': requestContext.builderId,
        },
      });

      if (!response.ok) {
        throw new Error('HTTP error fetching configs!');
      }

      const data: SummaryConfigs = response.data as SummaryConfigs;
      setConfigs(data);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error(`An unknown error occurred during fetch: ${err}`);
      telemetry.trackError({
        message: 'Error fetching configs',
        error: err,
      });
      setError(error);
      setConfigs(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, ctwFetch, requestContext.authToken, requestContext.builderId, telemetry]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const updateConfigs = useCallback(
    async (newConfigs: SummaryConfigs) => {
      if (!apiUrl) {
        setError(new Error('Builder ID is required to update.'));
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await ctwFetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
            'Zus-Account': requestContext.builderId,
          },
          body: JSON.stringify(newConfigs),
        });

        if (!response.ok) {
          const err = new Error(`HTTP error updating configs. Content: ${response.data}`);
          telemetry.trackError({
            message: 'HTTP error updating configs',
            error: err,
            context: {
              response: response.data,
            },
          });
          throw err;
        }

        const updatedData: SummaryConfigs = response.data as SummaryConfigs;
        setConfigs(updatedData);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('An unknown error occurred during update');
        telemetry.trackError({
          message: 'HTTP error updating configs',
          error: err,
        });
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, ctwFetch, requestContext.authToken, requestContext.builderId, telemetry],
  );

  return { configs, loading, error, updateConfigs, fetchConfigs };
}

export default useSummaryConfig;
